import Home from "@components/Home"
import { useRouteShellContext } from "@components/routeShellContext"
import { SiteShell } from "@components/SiteShell"
import { homePageQueryOptions } from "@data/publicPageDataQuery"
import type { HomePageSettings, SupportedLocale as Lang } from "@site-types/content"
import type { HomePageData } from "@site-types/publicPageData"
import { createFileRoute } from "@tanstack/react-router"
import { localizedRouteHead } from "@utils/routeHead"

export const Route = createFileRoute("/$locale/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(homePageQueryOptions(params.locale as "en" | "ja")),
  head: ({ params }) => {
    const locale = params.locale as "en" | "ja"
    return localizedRouteHead({ locale, page: "index", path: `/${locale}` })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const data = Route.useLoaderData() as HomePageData

  return (
    <SiteShell>
      <HomePage data={data} />
    </SiteShell>
  )
}

const requiredHomeSettingsFields: (keyof Omit<HomePageSettings, "language">)[] = [
  "homePageBrandImage",
  "homePageFluxBackground",
  "homePageFluxLogo",
  "homePageFutarokuBackground",
  "homePageFutarokuLogo",
  "homePageGamersImage",
  "homePagePlaydataBackground",
  "homePagePlaydataLogo",
  "homePageTaiyoroBackground",
  "homePageTaiyoroLogo",
]

type HomePageImageSetting = keyof Omit<HomePageSettings, "language">

const localHomeSettings: Record<Lang, HomePageSettings> = {
  en: {
    language: "en",
    homePageBrandImage: { asset: { url: "/images/home/playbrain-symbol.svg" } },
    homePageFluxBackground: { asset: { url: "/images/home/gamers.webp" } },
    homePageFluxLogo: { asset: { url: "/images/flux-logo.svg" } },
    homePageFutarokuBackground: { asset: { url: "/images/home/gamers.webp" } },
    homePageFutarokuLogo: { asset: { url: "/images/futaroku-logo.svg" } },
    homePageGamersImage: { asset: { url: "/images/home/gamers.webp" } },
    homePagePlaydataBackground: { asset: { url: "/images/home/playdata-background.webp" } },
    homePagePlaydataLogo: { asset: { url: "/images/playdata-logo.svg" } },
    homePageTaiyoroBackground: { asset: { url: "/images/home/taiyoro-background.webp" } },
    homePageTaiyoroLogo: { asset: { url: "/images/taiyoro-logo.svg" } },
  },
  ja: {
    language: "ja",
    homePageBrandImage: { asset: { url: "/images/home/playbrain-symbol.svg" } },
    homePageFluxBackground: { asset: { url: "/images/home/gamers.webp" } },
    homePageFluxLogo: { asset: { url: "/images/flux-logo.svg" } },
    homePageFutarokuBackground: { asset: { url: "/images/home/gamers.webp" } },
    homePageFutarokuLogo: { asset: { url: "/images/futaroku-logo.svg" } },
    homePageGamersImage: { asset: { url: "/images/home/gamers.webp" } },
    homePagePlaydataBackground: { asset: { url: "/images/home/playdata-background.webp" } },
    homePagePlaydataLogo: { asset: { url: "/images/playdata-logo.svg" } },
    homePageTaiyoroBackground: { asset: { url: "/images/home/taiyoro-background.webp" } },
    homePageTaiyoroLogo: { asset: { url: "/images/taiyoro-logo.svg" } },
  },
}

export function HomePage({ data }: { data: HomePageData }) {
  const { locale } = useRouteShellContext()
  const cmsSettings = data.homeSettings?.[0] ?? localHomeSettings[locale]
  const settings = validateHomeSettings(
    locale,
    mergeHomeSettings(locale, cmsSettings, localHomeSettings[locale])
  )

  return <Home settings={settings} />
}

function hasImageSetting(settings: HomePageSettings, field: HomePageImageSetting) {
  const image = settings[field]?.asset
  return Boolean(image?.objectKey || image?.url)
}

function mergeHomeImageSetting(
  cmsValue: HomePageSettings[HomePageImageSetting],
  fallback: HomePageSettings[HomePageImageSetting]
) {
  if (!cmsValue || !cmsValue.asset) return fallback
  const hasAssetData = Boolean(cmsValue.asset.objectKey || cmsValue.asset.url)
  return hasAssetData ? cmsValue : fallback
}

function mergeHomeSettings(
  locale: "en" | "ja",
  cmsSettings: HomePageSettings,
  fallback: HomePageSettings
): HomePageSettings {
  return {
    language: cmsSettings.language ?? fallback.language ?? locale,
    homePageBrandImage: mergeHomeImageSetting(cmsSettings.homePageBrandImage, fallback.homePageBrandImage),
    homePageFluxBackground: mergeHomeImageSetting(
      cmsSettings.homePageFluxBackground,
      fallback.homePageFluxBackground
    ),
    homePageFluxLogo: mergeHomeImageSetting(cmsSettings.homePageFluxLogo, fallback.homePageFluxLogo),
    homePageFutarokuBackground: mergeHomeImageSetting(
      cmsSettings.homePageFutarokuBackground,
      fallback.homePageFutarokuBackground
    ),
    homePageFutarokuLogo: mergeHomeImageSetting(
      cmsSettings.homePageFutarokuLogo,
      fallback.homePageFutarokuLogo
    ),
    homePageGamersImage: mergeHomeImageSetting(cmsSettings.homePageGamersImage, fallback.homePageGamersImage),
    homePagePlaydataBackground: mergeHomeImageSetting(
      cmsSettings.homePagePlaydataBackground,
      fallback.homePagePlaydataBackground
    ),
    homePagePlaydataLogo: mergeHomeImageSetting(
      cmsSettings.homePagePlaydataLogo,
      fallback.homePagePlaydataLogo
    ),
    homePageTaiyoroBackground: mergeHomeImageSetting(
      cmsSettings.homePageTaiyoroBackground,
      fallback.homePageTaiyoroBackground
    ),
    homePageTaiyoroLogo: mergeHomeImageSetting(cmsSettings.homePageTaiyoroLogo, fallback.homePageTaiyoroLogo),
  }
}

function validateHomeSettings(locale: "en" | "ja", settings?: HomePageSettings): HomePageSettings {
  if (!settings) {
    throw new Error(`Missing homeSettings payload for locale "${locale}".`)
  }

  const missing = requiredHomeSettingsFields.filter((field) => {
    return !hasImageSetting(settings, field)
  })

  if (missing.length > 0) {
    throw new Error(`Missing required homeSettings fields for locale "${locale}": ${missing.join(", ")}.`)
  }

  return settings
}
